import React, { useEffect, useMemo, useState } from 'react';
import { useTaskStore } from '../../../stores/taskStore';
import { useGoalStore } from '../../../stores/goalStore';
import type { Task } from '../../../types';

interface PausedGoalTaskPanelProps {
  isOpen: boolean;
  onClose: () => void;
  goalId: string | null;
  targetElement?: HTMLElement | null;
}

export const PausedGoalTaskPanel: React.FC<PausedGoalTaskPanelProps> = ({
  isOpen,
  onClose,
  goalId,
  targetElement
}) => {
  const { tasks, taskTypes } = useTaskStore();
  const { goals, getSubGoals } = useGoalStore();
  const [position, setPosition] = useState({ top: 80, left: 320 });

  // 获取目标信息
  const goal = useMemo(() => {
    return goalId ? goals.find(g => g.id === goalId) : null;
  }, [goals, goalId]);

  // 获取目标及其子目标的所有任务
  const goalTasks = useMemo(() => {
    if (!goalId) return [];

    // 获取直接关联的任务
    const directTasks = tasks.filter(task => task.goalId === goalId);

    // 获取子目标的任务
    const subGoals = getSubGoals(goalId);
    const subGoalTasks = subGoals.flatMap(subGoal =>
      tasks.filter(task => task.goalId === subGoal.id)
    );

    return [...directTasks, ...subGoalTasks];
  }, [tasks, goalId, getSubGoals]);

  // 按状态分组任务
  const tasksByStatus = useMemo(() => {
    return {
      TODO: goalTasks.filter(task => task.status === 'TODO'),
      IN_PROGRESS: goalTasks.filter(task => task.status === 'IN_PROGRESS'),
      DONE: goalTasks.filter(task => task.status === 'DONE')
    };
  }, [goalTasks]);

  // 计算面板位置
  const updatePosition = () => {
    if (goalId) {
      // 重新查找当前DOM中的目标元素
      const currentElement = document.querySelector(`[data-goal-id="${goalId}"]`);

      if (currentElement) {
        const rect = currentElement.getBoundingClientRect();

        // 估算面板高度：头部64px + 任务数量 * 72px + 内边距24px
        const estimatedPanelHeight = 64 + (goalTasks.length === 0 ? 60 : goalTasks.length * 72) + 24;

        const newPosition = {
          top: rect.top + (rect.height / 2) - (estimatedPanelHeight / 2),
          left: rect.right + 16 // 增加到16px间距，让面板远离侧栏
        };

        setPosition(newPosition);
      } else {
        setPosition({ top: 80, left: 320 });
      }
    } else {
      setPosition({ top: 80, left: 320 });
    }
  };

  // 监听滚动和元素位置变化
  useEffect(() => {
    if (!isOpen || !goalId) return;

    // 初始化位置
    updatePosition();

    // 监听滚动事件
    const handleScroll = () => {
      updatePosition();
    };

    // 监听窗口大小变化
    const handleResize = () => {
      updatePosition();
    };

    // 添加事件监听器
    window.addEventListener('scroll', handleScroll, true); // 使用捕获模式监听所有滚动
    window.addEventListener('resize', handleResize);

    // 清理监听器
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, goalId, goalTasks.length]);


  if (!isOpen || !goalId || !goal) return null;

  // 计算面板内容高度（不设死高度，让内容自然撑开）
  const headerHeight = 64; // 头部高度
  const taskHeight = 72; // 每个任务项的实际高度
  const padding = 24; // 内边距
  const emptyStateHeight = 60; // 空状态的高度

  const contentHeight = goalTasks.length === 0
    ? emptyStateHeight
    : goalTasks.length * taskHeight;

  const totalHeight = headerHeight + contentHeight + padding;

  return (
    <div
      className="fixed w-80 bg-neutral-25 border border-gray-200 rounded-lg shadow-lg z-30"
      style={{
        top: position.top,
        left: position.left,
        height: 'auto',
        maxHeight: '80vh'
      }}
    >
      {/* 简洁头部 */}
      <div className="p-3 border-b border-gray-100 flex-shrink-0">
        <h3 className="text-sm font-medium text-gray-700">暂停目标任务</h3>
        <p className="text-xs text-gray-500 mt-1 truncate">{goal.title}</p>
      </div>

      {/* 任务列表 */}
      <div className="p-3 space-y-2">
        {goalTasks.length === 0 ? (
          <div className="text-center py-3">
            <p className="text-xs text-gray-400">此目标下暂无任务</p>
          </div>
        ) : (
          goalTasks.map(task => (
            <div
              key={task.id}
              className="p-2 bg-white border border-gray-100 rounded-md shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-medium text-gray-900 truncate">{task.title}</h4>
                  {task.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">{task.description}</p>
                  )}
                  <div className="flex items-center gap-1 mt-1">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                      task.status === 'TODO'
                        ? 'bg-blue-100 text-blue-700'
                        : task.status === 'IN_PROGRESS'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {task.status === 'TODO' ? '待办' : task.status === 'IN_PROGRESS' ? '进行中' : '已完成'}
                    </span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                      task.priority === 'HIGH'
                        ? 'bg-red-100 text-red-700'
                        : task.priority === 'MEDIUM'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {task.priority === 'HIGH' ? '高' : task.priority === 'MEDIUM' ? '中' : '低'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};